import { useAtom } from 'jotai';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import {
  Box,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { createRoot } from 'react-dom/client';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import useSupercluster from 'use-supercluster';

import {
  faChevronCircleLeft,
  faChevronCircleRight,
  faHelmetSafety,
  faLocationDot,
  faMap,
} from '@fortawesome/free-solid-svg-icons';

import '../scss/Pages/_dashboard.scss';
import customAxios from '../utils/customAxios';
import { GlobalVariables } from '../globalVar';
import m1 from '../media/Icons/cluster_images/m1.png';
import m2 from '../media/Icons/cluster_images/m2.png';
import m3 from '../media/Icons/cluster_images/m3.png';
import m4 from '../media/Icons/cluster_images/m4.png';
import m5 from '../media/Icons/cluster_images/m5.png';
import { pinsAtom } from '../stores/geolocationStore';
import { useTranslationHook } from '../hooks/UseTranslationHook';
import { VisitsBySiteAxios } from '../components/Tables/VisitsBySite';
import { TrackingVTableAxios } from '../components/Tables/TrackingVisitsTable';
import { TrackingTableAxios } from '../components/Tables/TrackingTechsTable';

const InfoWindowContent = ({ loading, tech, pin }) => {
  if (loading) {
    return (
      <Box pt="0" pb="8px" px="8px" fontSize="14px">
        <Typography>
          <Skeleton width={180} />
        </Typography>
        <Typography>
          <Skeleton width={150} />
        </Typography>
        <Typography>
          <Skeleton width={200} />
        </Typography>
      </Box>
    );
  }
  if (!tech) return null;
  return (
    <Box pt="0" pb="8px" px="8px" fontSize="14px">
      <Typography>
        <strong>Nombre:</strong> {tech.name} {tech.first_surname}
      </Typography>
      <Typography>
        <strong>Correo:</strong> {tech.email}
      </Typography>
      <Typography>
        <strong>Teléfono:</strong> {tech.telefono}
      </Typography>
      <Typography>
        <strong>Ubicación:</strong> ({pin.lat.toFixed(4)}, {pin.long.toFixed(4)}
        )
      </Typography>
    </Box>
  );
};

const TechMarker = memo(({ pin, onHover, onLeave, onClick, fillColor }) => (
  <Marker
    key={pin.techId}
    position={{ lat: pin.lat, lng: pin.long }}
    onMouseOver={() => onHover(pin)}
    onMouseOut={onLeave}
    onClick={() => onClick(pin.techId)}
    icon={{
      path: 'M256 32c-17.7 0-32 14.3-32 32v2.3 99.6c0 5.6-4.5 10.1-10.1 10.1c-3.6 0-7-1.9-8.8-5.1L157.1 87C83 123.5 32 199.8 32 288v64H544l0-66.4c-.9-87.2-51.7-162.4-125.1-198.6l-48 83.9c-1.8 3.2-5.2 5.1-8.8 5.1c-5.6 0-10.1-4.5-10.1-10.1V66.3 64c0-17.7-14.3-32-32-32H256zM16.6 384C7.4 384 0 391.4 0 400.6c0 4.7 2 9.2 5.8 11.9C27.5 428.4 111.8 480 288 480s260.5-51.6 282.2-67.5c3.8-2.8 5.8-7.2 5.8-11.9c0-9.2-7.4-16.6-16.6-16.6H16.6z',
      fillColor: fillColor,
      fillOpacity: 1,
      strokeWeight: 0,
      scale: 0.05,
    }}
  />
));

const Dashboard = () => {
  // === STATES ===
  const [allTechA, setAllTechA] = useState([]);
  const [allTechNA, setAllTechNA] = useState([]);
  const [allVisitsA, setAllVisitsA] = useState([]);
  const [allVisitsNA, setAllVisitsNA] = useState([]);

  const [mapCenter, setMapCenter] = useState({
    lat: 24.5697788,
    lng: -100.5700995,
  });
  const [mapZoom, setMapZoom] = useState(6);
  const [bounds, setBounds] = useState(null);

  const [zoomControl, setZoomControl] = useState(true);
  const [gestureHandling, setGestureHandling] = useState('greedy');
  const [tabIndex, setTabIndex] = useState(0);
  const [isTablesCollapsed, setIsTablesCollapsed] = useState(true);
  const [clusterClickFlag, setClusterClickFlag] = useState(false);
  const [loadingTechHover, setLoadingTechHover] = useState(false);

  const [pins] = useAtom(pinsAtom);
  const [visitsPins, setVisitsPins] = useState([]);

  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const infoWindowContainerRef = useRef(null);
  const infoWindowRootRef = useRef(null);

  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const {
    titleDashboardString,
    techsString,
    visitsString,
    dashboardViewMapStr,
    dashboardShowTMarkersStr,
    dashboardHideTMarkersStr,
  } = useTranslationHook();

  useEffect(() => {
    fetchUserMapConfig();
  }, []);

  const fetchUserMapConfig = async () => {
    try {
      const userId = localStorage.getItem('currUserLogged');
      const endpoint = `${GlobalVariables.getURL()}user-config/${userId}`;
      const response = await customAxios.get(endpoint, {
        headers: { Authorization: 'bearer ' + localStorage.getItem('token') },
      });
      const mapConfig = response.data.data.modules.Mapa[0];
      const coords = JSON.parse(mapConfig.value);
      setMapCenter({ lat: coords[0], lng: coords[1] });
    } catch (error) {
      console.log('🚀 ~ error:', error);
    }
  };

  // === HANDLERS ===
  const handleTabChange = (event, newTabIndex) => setTabIndex(newTabIndex);
  const toggleTablesCollapse = () => setIsTablesCollapsed(!isTablesCollapsed);
  const handleVisitsDataChange = (newVisitsData) =>
    setVisitsPins(newVisitsData);

  const receiveTechADataTable = (data) => setAllTechA(data);
  const receiveTechNADataTable = (data) => setAllTechNA(data);
  const receiveVisitsADataTable = (data) => setAllVisitsA(data);
  const receiveVisitsNADataTable = (data) => setAllVisitsNA(data);
  const receiveVisitsCDataTable = () => {};

  const techStatusDictionary = useMemo(() => {
    const dict = new Map();
    allTechNA.forEach((tech) => dict.set(tech.tech_id, { isNA: true }));
    allTechA.forEach((tech) => dict.set(tech.tech_id, { isA: true }));
    return dict;
  }, [allTechA, allTechNA]);

  const findTechPinFillColor = useCallback(
    (techId) => {
      const status = techStatusDictionary.get(techId);
      if (status?.isA) return 'red';
      if (status?.isNA) return '#57A65C';
      return '#c6cad1';
    },
    [techStatusDictionary],
  );

  const visitStatusDictionary = useMemo(() => {
    const dict = new Map();
    allVisitsNA.forEach((visit) => dict.set(visit.visit_id, { isNA: true }));
    allVisitsA.forEach((visit) => {
      dict.set(visit.visit_id, {
        isA: true,
        isCompleted: visit.visit_status === 'Técnico ha terminado',
        isCanceled: visit.visit_status === 'Visita cancelada',
      });
    });
    return dict;
  }, [allVisitsA, allVisitsNA]);

  const findVisitPinFillColor = useCallback(
    (visitId) => {
      const status = visitStatusDictionary.get(visitId);
      if (status?.isCompleted) return 'green';
      if (status?.isCanceled) return 'black';
      if (status?.isNA) return 'red';
      if (status?.isA) return '#f5a43c';
      return 'gray';
    },
    [visitStatusDictionary],
  );

  // =========================================================================
  // SUPER CLUSTER (use-supercluster)
  // =========================================================================

  // GeoJSON
  const points = useMemo(() => {
    return visitsPins.map((visit) => ({
      type: 'Feature',
      properties: {
        cluster: false,
        visitId: visit.visit_id,
        client: visit.client,
        site: visit.site,
        siteId: visit.site_id,
      },
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(visit.longitude), parseFloat(visit.latitude)],
      },
    }));
  }, [visitsPins]);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: mapZoom,
    options: { radius: 75, maxZoom: 20 },
  });
  // =========================================================================

  // === MAP CONFIG ===
  const mapStyles = [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ];
  const mapOptions = useMemo(
    () => ({
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl,
      gestureHandling,
      styles: mapStyles,
    }),
    [zoomControl, gestureHandling],
  );

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setTimeout(() => {
      if (mapRef.current) {
        const bounds = mapRef.current.getBounds();
        if (bounds) {
          setBounds([
            bounds.getSouthWest().lng(),
            bounds.getSouthWest().lat(),
            bounds.getNorthEast().lng(),
            bounds.getNorthEast().lat(),
          ]);
        }
      }
    }, 500);

    if (infoWindowRootRef.current) {
      infoWindowRootRef.current.unmount();
      infoWindowRootRef.current = null;
      infoWindowContainerRef.current = null;
    }
    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, []);

  const handleClickOnMap = () => {
    if (clusterClickFlag) {
      localStorage.removeItem('siteIdOfCluster');
      setIsTablesCollapsed(true);
      setClusterClickFlag(false);
      setZoomControl(true);
      setGestureHandling('greedy');
      setMapCenter({ lat: 20.587972, lng: -100.387929 });
      setMapZoom(13);
    }
  };

  const handleTechMouseOver = useCallback(async (pin) => {
    if (!infoWindowRef.current) return;
    setLoadingTechHover(true);

    if (!infoWindowContainerRef.current) {
      infoWindowContainerRef.current = document.createElement('div');
      infoWindowRootRef.current = createRoot(infoWindowContainerRef.current);
    }

    infoWindowRootRef.current.render(
      <InfoWindowContent loading={true} tech={null} pin={pin} />,
    );
    infoWindowRef.current.setContent(infoWindowContainerRef.current);
    infoWindowRef.current.setPosition({ lat: pin.lat, lng: pin.long });

    if (!infoWindowRef.current.getMap()) {
      infoWindowRef.current.open({ map: mapRef.current, anchor: null });
    }

    try {
      const endpoint = `${GlobalVariables.getURL()}listTech/${pin.techId}`;
      const response = await customAxios.get(endpoint, {
        headers: { Authorization: 'bearer ' + localStorage.getItem('token') },
      });
      infoWindowRootRef.current.render(
        <InfoWindowContent
          loading={false}
          tech={response.data.techs}
          pin={pin}
        />,
      );
    } catch (error) {
      console.error('Error loading tech details:', error);
      infoWindowRef.current.setContent(
        '<Typography>Error al cargar información.</Typography>',
      );
    } finally {
      setLoadingTechHover(false);
    }
  }, []);

  const handleTechMouseOut = useCallback(() => {
    if (infoWindowRef.current) infoWindowRef.current.close();
  }, []);

  return (
    <div className="dashboard-container">
      <Helmet>
        <title>{titleDashboardString}</title>
      </Helmet>
      <ToastContainer />
      <div className="dashboard-visits">
        <div className="dashboard-card">
          <div className="card-front">
            {/* === Map === */}
            <div className={`map ${isTablesCollapsed ? 'expanded' : ''}`}>
              {!isLoaded ? (
                <div className="spinner">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              ) : (
                <GoogleMap
                  ref={mapRef}
                  zoom={mapZoom}
                  center={mapCenter}
                  mapContainerClassName="map-container"
                  options={mapOptions}
                  onClick={handleClickOnMap}
                  onLoad={onMapLoad}
                  onIdle={() => {
                    if (mapRef.current) {
                      const bounds = mapRef.current.getBounds();
                      if (bounds) {
                        setBounds([
                          bounds.getSouthWest().lng(),
                          bounds.getSouthWest().lat(),
                          bounds.getNorthEast().lng(),
                          bounds.getNorthEast().lat(),
                        ]);
                      }
                      setMapZoom(mapRef.current.getZoom());
                    }
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: clusterClickFlag
                        ? 'space-between'
                        : 'right',
                    }}
                  >
                    {clusterClickFlag && (
                      <div className="show-pin-table">
                        <Tooltip
                          title={dashboardViewMapStr}
                          placement="right"
                          arrow
                        >
                          <IconButton onClick={handleClickOnMap}>
                            <FontAwesomeIcon icon={faMap} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                    <div className="show-pin-table">
                      <Tooltip
                        title={
                          isTablesCollapsed
                            ? dashboardShowTMarkersStr
                            : dashboardHideTMarkersStr
                        }
                        placement="left"
                        arrow
                      >
                        <IconButton onClick={toggleTablesCollapse}>
                          <FontAwesomeIcon
                            icon={
                              isTablesCollapsed
                                ? faChevronCircleLeft
                                : faChevronCircleRight
                            }
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {clusters.map((cluster) => {
                    const [longitude, latitude] = cluster.geometry.coordinates;
                    const { cluster: isCluster, point_count: pointCount } =
                      cluster.properties;

                    if (isCluster) {
                      let clusterIcon = m1;
                      if (pointCount > 10) clusterIcon = m2;
                      if (pointCount > 50) clusterIcon = m3;
                      if (pointCount > 100) clusterIcon = m4;
                      if (pointCount > 500) clusterIcon = m5;

                      return (
                        <Marker
                          key={`cluster-${cluster.id}`}
                          position={{ lat: latitude, lng: longitude }}
                          onClick={() => {
                            const expansionZoom = Math.min(
                              supercluster.getClusterExpansionZoom(cluster.id),
                              20,
                            );
                            mapRef.current.setZoom(expansionZoom);
                            mapRef.current.panTo({
                              lat: latitude,
                              lng: longitude,
                            });
                          }}
                          icon={{
                            url: clusterIcon,
                            scaledSize: new window.google.maps.Size(40, 40),
                          }}
                          label={{
                            text: String(pointCount),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '14px',
                          }}
                        />
                      );
                    }

                    return (
                      <Marker
                        options={{ siteId: cluster.properties.siteId }}
                        key={`visit-${cluster.properties.visitId}`}
                        position={{ lat: latitude, lng: longitude }}
                        icon={{
                          path: 'M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z',
                          fillColor: findVisitPinFillColor(
                            cluster.properties.visitId,
                          ),
                          fillOpacity: 1,
                          strokeWeight: 0,
                          scale: 0.06,
                        }}
                        title={`${cluster.properties.client}, ${cluster.properties.site}`}
                        onClick={() =>
                          navigate(
                            `/visits/details/${cluster.properties.visitId}`,
                          )
                        }
                      />
                    );
                  })}

                  {pins.map((pin) => (
                    <TechMarker
                      key={pin.techId}
                      pin={pin}
                      fillColor={findTechPinFillColor(pin.techId)}
                      onHover={handleTechMouseOver}
                      onLeave={handleTechMouseOut}
                      onClick={(id) => navigate(`/tech/details/${id}`)}
                    />
                  ))}
                </GoogleMap>
              )}
            </div>

            {/* === Tables === */}
            <div className={`tables ${isTablesCollapsed ? 'collapsed' : ''}`}>
              {clusterClickFlag ? (
                <div className="table-visits-bysite">
                  <VisitsBySiteAxios />
                </div>
              ) : (
                <>
                  <Tabs
                    className="tabs-container"
                    value={tabIndex}
                    onChange={handleTabChange}
                    textColor="inherit"
                    variant="fullWidth"
                    TabIndicatorProps={{ sx: { backgroundColor: '#4a4a4a' } }}
                    sx={{
                      '& button': { color: '#4a4a4a' },
                      '& button:active': { color: '#dc1f0f' },
                      '& button:Mui-selected': { color: '#dc1f0f' },
                    }}
                  >
                    <Tab
                      label={
                        <span>
                          <FontAwesomeIcon icon={faHelmetSafety} />{' '}
                          {techsString}
                        </span>
                      }
                    ></Tab>
                    <Tab
                      label={
                        <span>
                          <FontAwesomeIcon icon={faLocationDot} />{' '}
                          {visitsString}
                        </span>
                      }
                    ></Tab>
                  </Tabs>

                  <div className="tables-container">
                    <div
                      className="table-techs"
                      style={{ display: tabIndex === 0 ? 'block' : 'none' }}
                    >
                      <TrackingTableAxios
                        pins={pins}
                        receiveTechADataTable={receiveTechADataTable}
                        receiveTechNADataTable={receiveTechNADataTable}
                      />
                    </div>
                    <div
                      className="table-visits"
                      style={{ display: tabIndex === 1 ? 'block' : 'none' }}
                    >
                      <TrackingVTableAxios
                        onVisitsDataChange={handleVisitsDataChange}
                        receiveVisitsADataTable={receiveVisitsADataTable}
                        receiveVisitsNADataTable={receiveVisitsNADataTable}
                        receiveVisitsCDataTable={receiveVisitsCDataTable}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
